import { useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo } from 'react'

import { getSubmissionClient } from '../blocks-renderers/clients/submission-client'
import { getQueryClient } from '../services/queryClient'
import { setAppError } from '../store/actions/setAppError'
import { selectAppParams } from '../store/selectors/runtime'
import { APP_ERROR } from '../store/types/enums'

import { useAppStoreSelector } from './useAppStoreSelector'

type Props = {
  /**
   * Allows the hook to be used multiple times throughout the app without causing side effects.
   *
   * If true, the hook will return the value and allow for manual invalidation but won't perform any of its side effects.
   * @default false
   */
  readOnly?: boolean
}

type ReturnType = {
  isLoading: boolean
  isError: boolean
  isNonceUsed: boolean | undefined
  /**
   * Calling this method will invalidate the nonce query, effectively running it again,
   * potentially resulting on its side effects.
   *
   * This is useful to call when a Journey has been submitted and is "restarted".
   */
  invalidate: () => void
}

/**
 * Responsible for checking whether a `nonce` has been used and performing the necessary
 * side effects (e.g. showing an error) accordingly.
 */
export const useCheckNonce = ({ readOnly = false }: Props = {}): ReturnType => {
  const nonce = useAppStoreSelector(selectAppParams)?.nonce

  const nonceQuery = useQuery({
    queryKey: ['nonce', nonce],
    queryFn: async ({ signal }) => {
      // mostly to coerce TS, should not happen
      if (!nonce) throw new Error('Expected nonce to be set')

      const response = await getSubmissionClient().getNonce(
        { nonce_id: nonce },
        undefined,
        { signal }
      )

      return response.data
    },
    enabled: Boolean(nonce)
  })

  useEffect(() => {
    if (!readOnly && nonceQuery.isError) {
      // eslint-disable-next-line no-console
      console.error('Failed to verify if nonce has been used', nonceQuery.error)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readOnly, nonceQuery.isError])

  useEffect(
    // fails if used previously
    () => {
      if (!readOnly && nonceQuery.data?.exists === true) {
        setAppError({
          error: APP_ERROR.JOURNEY_NONCE,
          reason: 'Journey has already been submitted.'
        })
      }
    },
    [readOnly, nonceQuery.data?.exists]
  )

  /**
   * Allows the caller to manually invalidate the nonce query without knowing
   * the associated key.
   *
   * This is useful to re-check after a submission has happened and the end-user
   * wants to restart the Journey.
   */
  const invalidate = useCallback(
    () =>
      void getQueryClient().invalidateQueries({
        queryKey: ['nonce', nonce]
      }),
    [nonce]
  )

  return useMemo(
    () => ({
      isLoading: nonceQuery.isLoading,
      isError: nonceQuery.isError,
      isNonceUsed: nonceQuery.data?.exists,
      invalidate
    }),
    [
      invalidate,
      nonceQuery.data?.exists,
      nonceQuery.isError,
      nonceQuery.isLoading
    ]
  )
}
